import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, ExternalLink, FileText, CheckCircle2, LogIn, Printer } from 'lucide-react';
import { API } from '../lib/api';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useA11y } from '../context/A11yContext';

export default function Handoff() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plainLanguage } = useA11y();
  const [share, setShare] = useState(null);
  const [err, setErr] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    api.get(`/share/${shareId}`).then(({ data }) => setShare(data)).catch(() => setErr(true));
  }, [shareId]);

  const claim = async () => {
    try {
      const { data } = await api.post(`/share/${shareId}/claim`);
      setClaimed(true);
      toast.success(`${data.added} program${data.added === 1 ? '' : 's'} added to My Applications`);
    } catch { toast.error('Could not add these to your tracker'); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="handoff-notfound"><h1 className="text-2xl font-bold">Checklist not found</h1><p className="text-muted-foreground mt-2">This link may be incorrect. Please ask your worker to send it again.</p></div>;
  if (!share) return <div className="max-w-3xl mx-auto px-4 py-16 text-muted-foreground">Loading checklist…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="handoff-page">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E5888]/10 text-[#1E5888] text-xs font-medium mb-4"><Users className="w-3.5 h-3.5" /> Shared by a community partner</div>
      <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>Hi {share.client_name}, here is your benefits checklist</h1>
      <p className="text-muted-foreground mt-2">Prepared by <strong className="text-foreground">{share.worker_name || 'your worker'}</strong>{share.organization ? ` at ${share.organization}` : ''} on {new Date(share.created_at).toLocaleDateString()}.</p>

      {share.notes && <Card className="p-4 mt-6 bg-[#FFF4E5] border-[#FFE0B2] text-sm" data-testid="handoff-notes"><strong>Note from {share.worker_name || 'your worker'}:</strong> {share.notes}</Card>}

      <Card className="p-5 mt-6 bg-card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="font-semibold" style={{fontFamily: 'Lexend'}}>Save this checklist to your own tracker</div>
          <div className="text-sm text-muted-foreground">Sign in (free) and these {share.programs.length} programs will appear in My Applications so you can track progress and store documents. <span className="block mt-1 text-xs" data-testid="handoff-consent-note">By saving, you agree that {share.worker_name || 'your worker'}{share.organization ? ` (${share.organization})` : ''} can see your application statuses — never your documents. You can pause this anytime from My Applications.</span></div>
        </div>
        {claimed ? <Button variant="outline" onClick={() => navigate('/applications')} data-testid="handoff-go-apps"><CheckCircle2 className="w-4 h-4 mr-2 text-primary" /> View My Applications</Button>
          : user ? <Button onClick={claim} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="handoff-claim-btn"><FileText className="w-4 h-4 mr-2" /> Add to My Applications</Button>
          : <Button onClick={() => { sessionStorage.setItem('post_login_redirect', `/handoff/${shareId}`); navigate('/login'); }} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="handoff-signin-btn"><LogIn className="w-4 h-4 mr-2" /> Sign in to save</Button>}
      </Card>
      <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Printer className="w-4 h-4" /> Prefer paper?
        <Button asChild size="sm" variant="outline" data-testid="handoff-print-pdf"><a href={`${API}/share/${shareId}/pdf`} target="_blank" rel="noopener noreferrer">Download printable checklist (PDF)</a></Button>
      </div>

      <div className="grid gap-4 mt-8">
        {share.programs.map((p, i) => (
          <Card key={p.id} className="p-5 bg-card" data-testid={`handoff-program-${p.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">{i + 1}</div>
              <div className="flex-1">
                <div className="flex gap-1.5 flex-wrap mb-1"><Badge variant="outline">{p.government}</Badge><Badge variant="outline">{p.category}</Badge></div>
                <h3 className="text-lg font-bold" style={{fontFamily: 'Lexend'}}>{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{plainLanguage ? p.plain_language : p.summary}</p>
                <div className="text-sm mt-2"><span className="text-muted-foreground">Est. value:</span> <span className="font-semibold text-primary">{p.estimated_amount}</span> · {p.effort}</div>
                <div className="mt-3 text-sm"><span className="font-medium">Documents to gather:</span>
                  <ul className="mt-1 grid sm:grid-cols-2 gap-1">{p.documents.map((d) => <li key={d} className="flex items-start gap-1.5 text-foreground/80"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />{d}</li>)}</ul>
                </div>
                <Button asChild size="sm" variant="outline" className="mt-3"><a href={p.application_url} target="_blank" rel="noopener noreferrer">Official application <ExternalLink className="w-3 h-3 ml-1" /></a></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
